'use client'

import { useEffect, useState, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ServiceCoursesProvider } from 'api/ServiceCoursesProvider'
import { BiAlarm, BiArrowBack } from "react-icons/bi"
import { IPublicClientApplication } from "@azure/msal-browser"
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal
} from "@azure/msal-react"
import Spinner from 'components/spinner'
import { loginRequest } from 'AuthConfig'
import { Link, useParams } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import { useLanguage } from 'contexts/LanguageContext'
import { useSubscription } from 'contexts/SubscriptionContext';
import { trackEvent } from 'utils/gtag'
import { ICourseDetails } from 'models/ICourseDetails'

export default function CourseDetails() {
  const { instance } = useMsal();
  const msal = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { lang, res } = useLanguage(); // Import the hook
  const courseId = useParams<{ courseid: string }>().courseid ?? "";
  const [course, setCourse] = useState<ICourseDetails>();
  const [participation, setParticipation] = useState<any>(undefined);
  const { subscription, subscriptionError } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [assistLoading, setAssistLoading] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [assistConfirmation, setAssistConfirmation] = useState<boolean>(false);
  const [assistError, setAssistError] = useState<boolean>(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [showParticipationWarning, setShowParticipationWarning] = useState<boolean>(false);
  const history = createBrowserHistory();


  useEffect(() => {
    UserSubscription();
  }, [subscription, pageError,subscriptionError]);

  useEffect(() => {
    if (subscription) {
      getParticipation();
    }
  }, [course, subscription]);

  useEffect(() => {
    setLoading(true)
    setIsMobile(window.innerWidth < 640)
    const getCourseById = async function () {
      try {
        // Fetch course details by ID 
        const courseDetails = await ServiceCoursesProvider.provider.getCourseById(courseId)
        setCourse(courseDetails)
      }
      catch (error) {
        setPageError(error?.toString() ?? null);
      }
      finally {
        setLoading(false)
      }
    }
    getCourseById()
  }, [courseId])

  useEffect(() => {
    const resizeHandler = () => {
      setIsMobile(window.innerWidth < 640)
    }
    // Add event listener
    window.addEventListener("resize", resizeHandler)
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", resizeHandler)
  }, [])

  async function assistSession(secondConfirmation = false) {
    if (subscription) {
      setAssistLoading(true);
      let assistRes: any;
      if (subscription.id && course?.id && (participation?.inscriptions === 0 || secondConfirmation)) {
        try {
          assistRes = await ServiceCoursesProvider.provider.assistSession(msal, lang, course.id, subscription.id);
          trackEvent('inscription',{
            course_id: course?.id,
            course_name: course?.titreLong[lang],
            subscription_id: subscription.id
          });
        } catch (error) {
          setPageError(error?.toString() ?? null);
        }
      }
      if (assistRes && (assistRes.status >= 200 && assistRes.status < 203)) {
        setAssistConfirmation(true)
      } else {
        if (participation?.inscriptions > 0) {
          setShowParticipationWarning(true);
        } else {
          setAssistError(true);
        }
      }
      setAssistLoading(false);
    }
  }

  function isAllowedToAssist() {
    let canAssist = false;
    if (course && subscription) {
      if (course.typesAbonnements && course.typesAbonnements.length > 0) {
        course.typesAbonnements.forEach((s: any) => {
          if (s && (s.id === subscription?.abonnement?.id)) {
            canAssist = true
          }
        })
      }
    }
    return canAssist;
  }

  async function getParticipation() {
    const canAssist = isAllowedToAssist();
    if (canAssist && !assistConfirmation && subscription && course?.id) {
      setAssistLoading(true);
      try {
        // Fetch user participation details
        const userParticipation = await ServiceCoursesProvider.provider.getParticipations(msal, lang, course.id);
        setParticipation(userParticipation);
      } catch (error) {
        setPageError(error?.toString() ?? null);
      }
      finally {
        setAssistLoading(false);
      }
    }
  }

  function UserSubscription() {
    const hasError = (pageError && pageError.trim() !== "")  || (subscriptionError && subscriptionError.trim() !== "");
    const canAssist = !hasError && isAllowedToAssist();

    return (canAssist) ? (
      <>
        {(assistConfirmation) ? (
          <div className="assist-confirmation">
            {res["Merci de vous être inscrit"]}
          </div>
        ) : (
          <span onClick={() => setModalOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10">
              <path id="Polygon_54" data-name="Polygon 54" d="M5,0l5,9H0Z" transform="translate(9) rotate(90)" fill="#ff5000" />
            </svg>&nbsp;
            {res["Je veux assister"]}
          </span>
        )}

      </>
    ) : (!hasError ? (
      <Link to={res.pagesLinks.subscribe + "?courseid=" + course?.id}>
        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10">
          <path id="Polygon_54" data-name="Polygon 54" d="M5,0l5,9H0Z" transform="translate(9) rotate(90)" fill="#ff5000" />
        </svg>&nbsp;
        {res["Non inclus dans votre abonnement"]}<br />
        {res["(cliquer sur Abonnez-vous)"]}
      </Link>
    ) :
      /* Show error if subscription is null */
      <div className="bg-red-600 text-white p-3 mb-4 rounded">
        {res['Erreur generique']}
      </div>
    );
  }

  function signInClickHandler(instance: IPublicClientApplication) {
    // Open B2C login with a popup window
    loginRequest.extraQueryParameters = {
      ui_locales: lang === 'fr' ? "fr-ca" : 'en-ca' // Change this to the desired locale
    };

    const popupWidth = 900;
    const popupHeight = 850;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);

    instance.loginPopup({
      ...loginRequest,
      popupWindowAttributes: {
        popupSize: { width: popupWidth, height: popupHeight },
        popupPosition: { left, top }
      }
    })
  }

  const displayHour = () => {
    const dateSession = (course) ? new Date(course.dateSession) : undefined

    if (!dateSession) {
      return null;
    }

    // Format hours and minutes from the dateSession
    const hours = dateSession.getHours().toString().padStart(2, '0');
    const minutes = dateSession.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  return (
    <section className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <div className="relative pt-32 pb-10">
          <div className="_max-w-3xl mx-auto _text-center">
            <button className="btn text-white mb-3 bg-gray-700 hover:bg-gray-800" onClick={() => history.back()}><BiArrowBack /></button>
            {(loading) ? (
              <Spinner />
            ) : null}
            {(course && course?.id) ? (
              <div className={(!isMobile) ? "flex" : "mobile-course-fix"}>
                <div className="relative h-full c-feature-post-left-wpr fade-in-up">
                  <img
                    src={(course?.imageUrl) ? course?.imageUrl : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="}
                    style={{ height: (!course?.imageUrl) ? "359px" : undefined, background: (!course?.imageUrl) ? '#222' : undefined }}
                    alt={(course?.titreLong) ? course?.titreLong[course.langue] : ""}
                  />
                  <div className="c-post-date-wpr">
                    <div className="c-post-cat ext">
                      <span className="text-black">{(course?.categoriePrincipale?.titre) ? course?.categoriePrincipale?.titre[lang] : ""}</span>
                    </div>
                    <div className="c-post-cat-date-wpr ext" style={{ background: (!course?.imageUrl) ? "#333333" : undefined }}>
                      <div className="text-white">{new Date((course?.date) ? course?.date : course?.dateSession).toLocaleString(lang + '-CA', { month: 'short', day: 'numeric' })}</div>
                      <div className="text-white" style={{ paddingTop: "5px" }}>{displayHour()}</div>
                    </div>
                  </div>
                </div>
                <div className="c-feature-post-right-wpr fade-in-up-delay">
                  <h3>{(course?.titreLong) ? course?.titreLong[course.langue] : ""}</h3>
                  <div className="c-formar-wpr">
                    <span className="text-orange-500">{res["Formateur"]}</span>
                    <ol>
                      <li>{course?.formateur}</li>
                    </ol>
                  </div>
                  <div className="c-post-cat-wpr">
                      <span className="text-orange-500">{res["Catégorie"]}</span>
                      <p>{(course?.categoriePrincipale?.titre) ? course?.categoriePrincipale?.titre[lang] : ""}</p>
                    </div>
                    <br />
                    <div data-wrapper="true" dir="ltr" className="actions-list">
                      {(course?.notes && course?.notes[course.langue]) ?
                        course?.notes[course.langue].split("\n").map((d: string, dI: number) => (
                          <div key={"d_" + dI} dangerouslySetInnerHTML={{ __html: d }}></div>

                        ))
                        : null
                      }
                    </div>
                    <div className="c-post-cat-wpr-link">
                      <AuthenticatedTemplate>
                        <UserSubscription />
                      </AuthenticatedTemplate>
                      <UnauthenticatedTemplate>
                        <a href="#courseUserSignin" id="courseUserSignin" onClick={(e) => {
                          e.preventDefault();
                          signInClickHandler(instance)
                        }} className="text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10">
                            <path id="Polygon_54" data-name="Polygon 54" d="M5,0l5,9H0Z" transform="translate(9) rotate(90)" fill="#ff5000" />
                          </svg>&nbsp;
                          {res["Je me connecte"]}
                        </a>
                      </UnauthenticatedTemplate>
                    </div>
                    <div className="c-post-cat-wpr text-white">
                      <div className={(!isMobile) ? "flex c-time" : "c-time"}>
                        <BiAlarm /> &nbsp;
                        <span className="grow">{(course?.duration) ? course?.duration : (course) ? course["duree"] : ""}</span>
                        {(!isMobile) ? (
                          <span className="grow" style={{ textAlign: "right" }}>{res["La formation est proposée régulièrement au calendrier"]}</span>
                        ) : null}
                      </div>
                      {(isMobile) ? (
                        <div className="text-sm">{res["La formation est proposée régulièrement au calendrier"]}</div>
                      ) : null}
                    </div>
                  </div>
                <Transition show={modalOpen} as={Fragment}>
                  <Dialog onClose={() => setModalOpen(false)}>
                    {/* Modal backdrop */}
                    <Transition.Child
                      className="fixed inset-0 z-[99999] bg-black bg-opacity-75 transition-opacity"
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition ease-out duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      aria-hidden="true"
                    />
                    {/* End: Modal backdrop */}

                    {/* Modal dialog */}
                    <Transition.Child
                      className={`fixed inset-0 z-[99999] overflow-hidden transform ${isMobile ? 'p-0' : 'flex items-center justify-center px-4 sm:px-6'}`}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="ttransition ease-out duration-200"
                      leaveFrom="oopacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <div className={`${isMobile ? 'w-full h-full' : 'max-w-6xl mx-auto flex items-center'} assist-modal bg-black`}>
                        <Dialog.Panel className={`w-full ${isMobile ? 'h-full flex flex-col' : 'max-h-full aspect-video'} modal-content overflow-hidden`}>
                          <div className="modal-header" style={{ gap: "4%" }}>
                            {
                              (() => {
                                const inscriptions = participation?.inscriptions || 0;
                                const presences = participation?.presences || 0;
                                const inscriptionsTitle = inscriptions > 0 ? `${res["Déjà inscrit(e)"]} (${inscriptions})` : '';
                                const presencesTitle = presences > 0 ? `${res["Déjà assisté(e)"]} (${presences})` : '';
                                const combinedTitle = inscriptionsTitle || presencesTitle ? `${inscriptionsTitle} ${presencesTitle}`.trim() : res["Je veux assister"];

                                const isAnyGreaterThanZero = inscriptions > 0 || presences > 0;
                                const titleClass = `modal-title${isAnyGreaterThanZero ? ' modal-title-red' : ''}`;

                                return (
                                  <h5 className={titleClass}>{combinedTitle}</h5>
                                );
                              })()
                            }
                            <button
                              aria-label="Close"
                              className="close"
                              type="button"
                              onClick={() => setModalOpen(false)}
                            >
                              <span aria-hidden={true}>×</span>
                            </button>
                          </div>
                          <div className="modal-body">
                            <h3>{(course?.titreLong) ? course?.titreLong[course.langue] : ""}</h3>
                            <div className="c-formar-wpr">
                              <span>{res["Formateur"]}</span>
                              <ol>
                                <li>{course?.formateur}</li>
                              </ol>
                            </div>
                            <div className="c-post-cat-wpr">
                              <span>{res["Catégorie"]}</span>
                              <p>{(course?.categoriePrincipale?.titre) ? course?.categoriePrincipale?.titre[lang] : ""}</p>
                            </div>
                            <div className="c-post-cat-date-wpr ext">
                              <div>{new Date((course?.date) ? course?.date : course?.dateSession).toLocaleString(lang + '-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                              <div style={{ paddingTop: "5px" }}>{displayHour()}</div>
                            </div>
                            {(assistError || showParticipationWarning) ? (
                              <div className="bg-red-600 p-3 mt-6 flex justify-between">
                                {(showParticipationWarning) ? res["Vous êtes déjà inscrit(e)"] : res["Erreur d'enregistrement !"]}
                                {(assistError) ? (
                                  <button
                                    aria-label="Close"
                                    className="close"
                                    type="button"
                                    onClick={() => setAssistError(false)}
                                  >
                                    <span aria-hidden={true}>×</span>
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                            {(assistLoading) ? (
                              <div style={{ position: "absolute", top: "30%", left: "40%" }}>
                                <Spinner />
                              </div>
                            ) : null}
                          </div>
                          <div className="modal-footer">
                            {(assistConfirmation) ? (
                              <div className="assist-confirmation">
                                {res["Merci de vous être inscrit"]}
                              </div>
                            ) : (
                              <>
                                <button type="button" className="btn btn-secondary text-white hover:bg-gray-800" onClick={() => { setModalOpen(false); setShowParticipationWarning(false) }} data-dismiss="modal">{showParticipationWarning ? res["Non"] : res["Annuler"]}</button>
                                {(showParticipationWarning) ? (
                                  <button type="button" disabled={assistLoading} className="btn btn-primary" onClick={() => { setShowParticipationWarning(false); assistSession(true); }} style={assistLoading ? { backgroundColor: "#eeeeee", color: "#dddddd" } : undefined}>{res["Oui"]}</button>
                                ) : (
                                  <button type="button" disabled={assistLoading} className="btn btn-primary" onClick={() => assistSession()} style={assistLoading ? { backgroundColor: "#eeeeee", color: "#dddddd" } : undefined}>{res["Oui, je veux m'inscrire"]}</button>
                                )}
                              </>
                            )}
                          </div>
                        </Dialog.Panel>
                      </div>
                    </Transition.Child>
                    {/* End: Modal dialog */}
                  </Dialog>
                </Transition>
              </div>
            ) : (
              <span>&nbsp; {(loading) ? "" : res["Aucune formation à afficher"]}</span>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}